import { ChatDocument, ChatDocumentSchema } from '@util/types/firestore/chat';
import { onSnapshot } from 'firebase/firestore';
import { useState, useMemo, useEffect } from 'react';
import { getChatsByChatIds } from '@util/firestore/messages/';
import { reportDashboardError } from '@util/reportDashboardError';

const useRealtimeChatsByChatIds = (chatIds?: string[]) => {
  const [realtimeChats, setChats] = useState<ChatDocument[] | null>(null);
  // Key on the ids themselves, not the array's identity. The caller rebuilds
  // this array whenever its paginated source changes, and re-subscribing on
  // an unchanged id set would blank the conversations it renders from here —
  // for as long as the new snapshot takes, or permanently if it fails.
  const idsKey = chatIds?.length ? chatIds.join(',') : '';
  const chatsQuery = useMemo(
    () => (idsKey ? getChatsByChatIds(idsKey.split(',')) : null),
    [idsKey]
  );

  useEffect(() => {
    setChats(null);
    // Same guard as useRealtimeChats: without ids there is nothing to watch,
    // and the placeholder query it used to send was a real Firestore read
    // whose only possible outcome was an empty result or a denial.
    if (!chatsQuery) return;

    let isCurrentListener = true;
    const unsubscribe = onSnapshot(
      chatsQuery,
      (snapshot) => {
        if (!isCurrentListener) return;
        const realTimeData = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            try {
              return ChatDocumentSchema.parse(data);
            } catch (e) {
              console.error('Invalid chat document:', e);
              return null;
            }
          })
          .filter((chat): chat is ChatDocument => chat !== null);
        setChats(realTimeData);
      },
      (err) => {
        if (!isCurrentListener) return;
        console.warn('[useRealtimeChatsByChatIds] snapshot error:', err);
        // Previously swallowed, on the same page this issue is about.
        reportDashboardError(
          'useRealtimeChatsByChatIds: chats snapshot failed',
          err
        );
      }
    );
    return () => {
      isCurrentListener = false;
      unsubscribe();
    };
  }, [chatsQuery]);

  return {
    realtimeChats,
  };
};

export default useRealtimeChatsByChatIds;
